import { Inject, Injectable } from '@angular/core';
import { from, of, Subject } from 'rxjs';
import { mapTo, mergeMap } from 'rxjs/operators';

import { ScriptService } from '../services/script.service';
import { WINDOW } from '../services/dom';
import {
  callTagCommanderTrackEvent,
  getTagCommanderVarsScriptCode,
  getVarsForTrackPageView,
  TAG_COMMANDER_VARS_MASK,
} from './tag-commander.helpers';
import { TAG_COMMANDER_CONFIG } from './tag-commander.providers';
import {
  TagCommanderClickType,
  TagCommanderConfig,
  TagCommanderEvent,
  TagCommanderEventOptions,
  TagCommanderEventType,
  TagCommanderVars,
  WindowWithTagCommander,
} from './tag-commander.types';

export const TAG_COMMANDER_CONTAINER_SCRIPT_ID = 'tagcommander-container';
export const TAG_COMMANDER_VARS_SCRIPT_ID = 'tagcommander-vars';

@Injectable({
  providedIn: 'root',
})
export class TagCommanderService {
  get containerUrl(): string {
    return this.tagCommandConfig.containerUrl;
  }

  get defaultVars(): Partial<TagCommanderVars> {
    return { ...this.tagCommandConfig.defaultVars };
  }

  readonly trackedEvent$ = new Subject<TagCommanderEvent>();

  private readonly containerScriptFirstLoaded$ = new Subject<void>();
  private containerScriptPromise: Promise<HTMLScriptElement>;
  private vars: Partial<TagCommanderVars> = {};

  constructor(
    @Inject(TAG_COMMANDER_CONFIG) readonly tagCommandConfig: TagCommanderConfig,
    @Inject(WINDOW) readonly win: WindowWithTagCommander,
    readonly scriptService: ScriptService,
  ) {
    this.monitorTrackEvents();
  }

  trackEvent(type: TagCommanderEventType, options: TagCommanderEventOptions): void {
    this.trackedEvent$.next({ type, options });
  }

  trackEventClick(type: TagCommanderClickType, label: string, xtLabel?: string): void {
    this.trackEvent(TagCommanderEventType.Click, {
      type_clic: type,
      xt_clic: xtLabel || label,
      clic: label,
    });
  }

  trackEventPage(label: string): void {
    this.trackEvent(TagCommanderEventType.Page, {
      xt_clic: '',
      clic: '',
      page_xtpage: label,
    });
  }

  trackEventPrint(campaignCode: string): void {
    this.trackEvent(TagCommanderEventType.Impression, {
      CAMPAGNE: `INT-${campaignCode}`,
    });
  }

  async trackPageView(page: string, chapter?: string, subChapter?: string, subSubChapter?: string): Promise<void> {
    const vars = getVarsForTrackPageView(page, chapter, subChapter, subSubChapter);

    this.patchVars(vars);

    await this.updateContainer();
  }

  async updateContainer(): Promise<void> {
    this.containerScriptPromise = this.updateContainerScript(this.containerUrl);

    await this.containerScriptPromise;

    if (!this.containerScriptFirstLoaded$.isStopped) {
      this.containerScriptFirstLoaded$.next();
      this.containerScriptFirstLoaded$.complete();
    }
  }

  patchVars(vars: Partial<TagCommanderVars>): void {
    this.updateVars({ ...this.vars, ...vars });
  }

  updateVars(vars: Partial<TagCommanderVars>): void {
    this.vars = vars;

    this.updateVarsScript({ ...TAG_COMMANDER_VARS_MASK, ...this.defaultVars, ...this.vars });
  }

  private monitorTrackEvents(): void {
    this.trackedEvent$
      .pipe(
        mergeMap(event =>
          this.containerScriptFirstLoaded$.isStopped ? of(event) : this.containerScriptFirstLoaded$.pipe(mapTo(event)),
        ),
        mergeMap(event => from(this.containerScriptPromise).pipe(mapTo(event))),
      )
      .subscribe(event => callTagCommanderTrackEvent(this.win, event));
  }

  private async updateContainerScript(containerUrl: string): Promise<HTMLScriptElement> {
    return this.scriptService.updateExternalScript(TAG_COMMANDER_CONTAINER_SCRIPT_ID, containerUrl);
  }

  private updateVarsScript(vars: TagCommanderVars): HTMLScriptElement {
    const code = getTagCommanderVarsScriptCode(vars);

    return this.scriptService.updateInlineScript(TAG_COMMANDER_VARS_SCRIPT_ID, code);
  }
}
